import { useEffect, useState, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Check, Play, Pause, X } from "lucide-react";
import { cn } from "@/lib/utils";

interface MusicTrack {
  id: string;
  title: string;
  artist: string | null;
  audio_url: string;
  occasion: string | null;
}

interface MusicPickerProps {
  occasion?: string;
  selectedMusicId: string | null;
  onSelect: (musicId: string | null) => void;
}

function isSpotifyUrl(url: string) {
  return /open\.spotify\.com\/track\/([a-zA-Z0-9]+)/.exec(url);
}

function isSoundCloudUrl(url: string) {
  return url.includes("soundcloud.com");
}

function isDirectAudioUrl(url: string) {
  return !isSpotifyUrl(url) && !isSoundCloudUrl(url);
}

export default function MusicPicker({ occasion, selectedMusicId, onSelect }: MusicPickerProps) {
  
  const [tracks, setTracks] = useState<MusicTrack[]>([]);
  const [loading, setLoading] = useState(true);
  const [playingId, setPlayingId] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    const fetch = async () => {
      const { data } = await supabase
        .from("story_music")
        .select("id, title, artist, audio_url, occasion")
        .eq("is_active", true)
        .order("sort_order", { ascending: true });
      setTracks((data as MusicTrack[]) || []);
      setLoading(false);
    };
    fetch();
  }, []);

  useEffect(() => {
    return () => {
      audioRef.current?.pause();
      audioRef.current = null;
    };
  }, []);

  const togglePlay = (track: MusicTrack) => {
    // For Spotify/SoundCloud, we toggle an inline embed instead
    if (!isDirectAudioUrl(track.audio_url)) {
      setPlayingId(playingId === track.id ? null : track.id);
      return;
    }

    if (playingId === track.id) {
      audioRef.current?.pause();
      setPlayingId(null);
      return;
    }
    audioRef.current?.pause();
    const audio = new Audio(track.audio_url);
    audio.volume = 0.5;
    audio.onended = () => setPlayingId(null);
    audio.play().catch(() => {});
    audioRef.current = audio;
    setPlayingId(track.id);
  };

  // Filter: show matching occasion first, then all others
  const sorted = [...tracks].sort((a, b) => {
    const aMatch = a.occasion === occasion ? 0 : 1;
    const bMatch = b.occasion === occasion ? 0 : 1;
    return aMatch - bMatch;
  });

  if (loading) {
    return (
      <div className="space-y-2">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-14 rounded-xl bg-muted animate-pulse" />
        ))}
      </div>
    );
  }

  if (tracks.length === 0) return null;

  const renderEmbed = (track: MusicTrack) => {
    const spotifyMatch = isSpotifyUrl(track.audio_url);
    if (spotifyMatch) {
      return (
        <iframe
          src={`https://open.spotify.com/embed/track/${spotifyMatch[1]}?utm_source=generator&theme=0`}
          width="100%"
          height="80"
          allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
          loading="lazy"
          className="rounded-lg mt-2 border-0"
        />
      );
    }
    if (isSoundCloudUrl(track.audio_url)) {
      return (
        <iframe
          width="100%"
          height="80"
          allow="autoplay"
          src={`https://w.soundcloud.com/player/?url=${encodeURIComponent(track.audio_url)}&color=%23ff5500&auto_play=false&hide_related=true&show_comments=false&show_user=false&show_reposts=false&show_teaser=false&visual=false`}
          className="rounded-lg mt-2 border-0"
        />
      );
    }
    return null;
  };

  return (
    <div className="space-y-2">
      {/* No music option */}
      <button
        onClick={() => {
          audioRef.current?.pause();
          setPlayingId(null);
          onSelect(null);
        }}
        className={cn(
          "w-full flex items-center gap-3 px-4 py-3 rounded-xl border transition-all text-start",
          !selectedMusicId
            ? "border-primary bg-primary/5"
            : "border-border hover:border-foreground/20"
        )}
      >
        <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center shrink-0">
          <X size={14} className="text-muted-foreground" />
        </div>
        <span className="text-sm text-foreground">No music</span>
        {!selectedMusicId && (
          <Check size={16} className="text-primary ms-auto" />
        )}
      </button>

      {sorted.map((track) => {
        const isSelected = selectedMusicId === track.id;
        const isPlaying = playingId === track.id;
        const isDirect = isDirectAudioUrl(track.audio_url);

        return (
          <div key={track.id}>
            <div
              className={cn(
                "flex items-center gap-3 px-4 py-3 rounded-xl border transition-all",
                isSelected
                  ? "border-primary bg-primary/5"
                  : "border-border hover:border-foreground/20"
              )}
            >
              {/* Play/Pause button */}
              <button
                onClick={() => togglePlay(track)}
                className="w-8 h-8 rounded-full bg-muted hover:bg-muted/80 flex items-center justify-center shrink-0 transition-colors"
              >
                {isPlaying ? (
                  <Pause size={14} className="text-foreground" />
                ) : (
                  <Play size={14} className="text-foreground ms-0.5" />
                )}
              </button>

              {/* Track info — clicking selects */}
              <button
                onClick={() => onSelect(track.id)}
                className="flex-1 text-start min-w-0"
              >
                <p className="text-sm font-medium text-foreground truncate">{track.title}</p>
                {track.artist && (
                  <p className="text-[11px] text-muted-foreground truncate">{track.artist}</p>
                )}
              </button>

              {!isDirect && (
                <span className="text-[9px] uppercase tracking-wider text-muted-foreground font-medium shrink-0">
                  {isSpotifyUrl(track.audio_url) ? "Spotify" : "SoundCloud"}
                </span>
              )}

              {track.occasion && track.occasion === occasion && (
                <span className="text-[9px] uppercase tracking-wider text-primary font-medium shrink-0">
                  Match
                </span>
              )}

              {isSelected && (
                <Check size={16} className="text-primary shrink-0" />
              )}
            </div>

            {/* Inline embed for Spotify/SoundCloud preview */}
            {isPlaying && !isDirect && renderEmbed(track)}
          </div>
        );
      })}
    </div>
  );
}
